import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { StatusBadge } from '../../components/common/StatusBadge';
import { Briefcase, CalendarCheck, Clock, BookOpen, Percent, ClipboardCheck } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts';
import { KpiCard, pct, LoadingCard, EmptyCard } from './shared';

export const FormateurTab = ({ data, loading }) => {
  if (loading) return <LoadingCard />;
  if (!data) return <EmptyCard message="Sélectionnez un formateur pour afficher son rapport" />;

  const chartData = data.sessions.map(s => ({
    code: s.code,
    'Jours planifiés': s.jours,
    'Cahiers remplis': s.cahiers_remplis,
  }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="formateur-kpis">
        <KpiCard icon={Briefcase} label="Sessions animées" value={data.kpis.sessions} testId="kpi-f-sessions" />
        <KpiCard icon={CalendarCheck} label="Jours animés" value={data.kpis.jours} color="emerald" testId="kpi-f-jours" />
        <KpiCard icon={Clock} label="Heures de formation" value={`${data.kpis.heures}h`} sub={`+ ${data.kpis.heures_autres}h autres · ${data.kpis.jours_conge} CP · ${data.kpis.jours_maladie} mal. · ${data.kpis.jours_off} OFF`} color="sky" testId="kpi-f-heures" />
        <KpiCard icon={BookOpen} label="Cahiers remplis" value={`${data.kpis.cahiers_remplis}/${data.kpis.jours}`} color="violet" testId="kpi-f-cahiers" />
        <KpiCard icon={ClipboardCheck} label="Présences validées" value={data.kpis.presences_validees} color="amber" testId="kpi-f-presences" />
        <KpiCard icon={Percent} label="Taux de remplissage" value={pct(data.kpis.taux_remplissage)} color="red" testId="kpi-f-remplissage" />
      </div>

      {chartData.length > 0 && (
        <Card className="border-0 shadow-sm" data-testid="chart-formateur">
          <CardHeader className="pb-2"><CardTitle className="text-base">Jours planifiés vs cahiers remplis par session</CardTitle></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="code" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Jours planifiés" fill="#6366f1" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Cahiers remplis" fill="#10b981" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <Card className="border-0 shadow-sm overflow-hidden">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Détail par session — {data.formateur.nom} {data.formateur.prenom}</CardTitle>
        </CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="formateur-table">
            <thead>
              <tr className="bg-slate-100 text-xs text-slate-500 uppercase tracking-wide">
                <th className="text-left px-4 py-2.5">Code</th>
                <th className="text-left px-2 py-2.5">Session</th>
                <th className="text-center px-2 py-2.5">Statut</th>
                <th className="text-center px-2 py-2.5">Candidats</th>
                <th className="text-center px-2 py-2.5">Jours</th>
                <th className="text-center px-2 py-2.5">Heures</th>
                <th className="text-center px-2 py-2.5">Cahiers remplis</th>
                <th className="text-center px-2 py-2.5">Prés. validées</th>
                <th className="text-center px-2 py-2.5">Remplissage</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.sessions.map(s => (
                <tr key={s.dossier_id} className="hover:bg-slate-50 transition-colors" data-testid={`formateur-row-${s.dossier_id}`}>
                  <td className="px-4 py-2.5 text-xs font-mono text-indigo-600">{s.code}</td>
                  <td className="px-2 py-2.5 font-medium text-slate-900">{s.intitule}</td>
                  <td className="text-center"><StatusBadge status={s.statut} /></td>
                  <td className="text-center text-slate-500">{s.candidats}</td>
                  <td className="text-center font-medium">{s.jours}</td>
                  <td className="text-center text-slate-500">{s.heures}h</td>
                  <td className="text-center font-medium">{s.cahiers_remplis}/{s.jours}</td>
                  <td className="text-center text-slate-500">{s.presences_validees}</td>
                  <td className="text-center font-medium">{pct(s.taux_remplissage)}</td>
                </tr>
              ))}
              {data.sessions.length === 0 && (
                <tr><td colSpan={9} className="text-center py-8 text-slate-400">Aucune session sur la période</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};
